import isNil from 'lodash/isNil';
import type { CSSProperties, ReactNode } from 'react';

import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/components/base/card';
import { Div } from '@ui/components/base/div';
import { Typography, TypographyVariant } from '@ui/components/base/typography';
import { cn } from '@ui/lib/utils';

enum ElevatedPaperVariant {
  DEFAULT = 'default',
  ACCENT = 'accent',
}

const VARIANT_STYLES: Record<ElevatedPaperVariant, CSSProperties> = {
  [ElevatedPaperVariant.DEFAULT]: {
    boxShadow: 'var(--shadow-1)',
    border: '1px solid var(--border)',
  },
  [ElevatedPaperVariant.ACCENT]: {
    boxShadow: 'none',
    border: '1px solid var(--accent-line)',
    background: 'linear-gradient(var(--accent-soft), var(--accent-soft)), var(--card)',
  },
};

interface ElevatedPaperProps {
  variant?: ElevatedPaperVariant;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
  fullHeight?: boolean;
  marginBottom?: number;
  padding?: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  contentClassName?: string;
}

const ElevatedPaper = ({
  variant = ElevatedPaperVariant.DEFAULT,
  style,
  className,
  children,
  fullHeight,
  marginBottom,
  padding,
  title,
  subtitle,
  icon,
  actions,
  flush = false,
  contentClassName,
}: ElevatedPaperProps) => (
  <Card
    className={cn(
      'overflow-x-auto text-left',
      isNil(title) ? 'mb-8' : 'flex min-w-0 flex-col',
      fullHeight && 'flex-1 min-h-0',
      className
    )}
    style={{
      borderRadius: 'var(--r-xl)',
      ...VARIANT_STYLES[variant],
      padding: padding ? `12px 16px` : undefined,
      marginBottom,
      ...style,
    }}
    padded={false}
  >
    {isNil(title) ? (
      children
    ) : (
      <>
        <CardHeader className={cn('px-5 pt-4', flush ? 'pb-3' : 'pb-0')}>
          <CardTitle className="flex min-w-0 items-center gap-2.5">
            {icon && <Div className="inline-flex shrink-0 text-fg-muted [&_svg]:size-4.5">{icon}</Div>}
            <Typography as="h4" variant={TypographyVariant.STRONG} className="truncate">
              {title}
            </Typography>
          </CardTitle>
          {subtitle && (
            <CardDescription className="truncate text-xs font-semibold text-fg-muted">{subtitle}</CardDescription>
          )}
          {actions && <CardAction className="flex flex-wrap items-center gap-2">{actions}</CardAction>}
        </CardHeader>
        <CardContent className={cn(flush ? 'px-0 pb-0' : 'px-5 pt-3 pb-4', contentClassName)}>{children}</CardContent>
      </>
    )}
  </Card>
);

export { ElevatedPaper, ElevatedPaperVariant };
